import firebase from '@/plugins/firebase';
import { MUTATE } from '@/store/modules/mutation_types';
import {
  FIREBASE_COLLECTION,
  FIREBASE_INDEXES,
  UNIVERSE_PROPERTIES,
  DASHBOARD_PROPERTIES,
  DASHBOARD_PROPERTIES_MODEL,
  DASHBOARD_DATE_PROPERTIES,
  UNIVERSE_PROPERTIES_OPTIONS,
  DASHBOARD_PROPERTIES_OPTIONS,
  FIREBASE_BOOLEAN
} from '@/utils/dashboard';
import { createLog, updateStatusErrorLog } from '../logs/actions';
import CryptoJS from 'crypto-js';

const db = firebase.firestore();

async function saveUniverses(data, commit, filters) {
  const universes = {};

  data.forEach(async obj => {
    const universesDataPromises = obj.docs.map(doc => doc.ref?.parent?.parent?.get());

    const universesData = await Promise.all(universesDataPromises);

    const universesDataMap = {};
    universesData.forEach(uniData => {
      universesDataMap[uniData.id] = uniData.data();
    });

    obj.docs.forEach(doc => {
      const universeId = doc.ref.parent.parent.id;
      if (!(universeId in universes)) {
        universes[universeId] = [];
      }

      const universeData = universesDataMap[universeId];

      universes[universeId].push({
        id: doc.id,
        data: doc.data(),
        ...universeData,
      });
    });

    let universe = { ...universes };

    universe = sanatizeUniverses(universe, filters);

    mutateUniverses(commit, universe);

    Boolean(Object.keys(universe).length) ? mutateSuccess(commit, true) : mutateSuccess(commit, false) ;

    mutateError(commit, false);

    changeInitialState(commit, false);
  });
}

export function clearUniverses({ commit }) {
  const universe = {};

  mutateUniverses(commit, universe);
  mutateSuccess(commit, false);
  changeInitialState(commit, true);
}

export async function getUniverses({ commit }, filters) {
  const query = await getQuery(filters);

  const hasDashboards = hasDashboardList(filters);

  try {
    return query.get()
      .then(async (data) => {
        if (data && data.docs && data.docs.length) {
          if (hasDashboards) {
            const dashboardArrays = await getUniversesDashboards(data, filters);
            saveUniverses(dashboardArrays, commit, filters);
            return true;
          }
          saveUniverses([data], commit, filters);
          return true;
        }
        mutateSuccess(commit, false);
        changeInitialState(commit, false);
        return true;
      })
      .catch(err => {
        console.error(err);
        mutateError(commit, true);
        changeInitialState(commit, false);
        return false;
      });
  } catch (err) {
    mutateError(commit, true);
    changeInitialState(commit, false);
    console.error(err);
  }
}

function clearEmptyFilters(filter) {
  const filterFields = Object.keys(filter);

  const clearedfilterFields = filterFields.reduce((clearedFilters, field) => {
    if (filter[field] === '' || filter[field] === 'Todos') {
      return clearedFilters;
    }
    clearedFilters[field] = filter[field];
    return clearedFilters;
  }, {});

  return clearedfilterFields;
}

function isUniverseProperty(property) {
  return property === UNIVERSE_PROPERTIES.universeId ||
  property === UNIVERSE_PROPERTIES.accountId ||
  property === UNIVERSE_PROPERTIES.isStaging;
}

function selectCollectionByFilter(filter) {
  const filterFields = Object.keys(clearEmptyFilters(filter));

  if (filterFields.some(isUniverseProperty)) {
    return {
      name: FIREBASE_COLLECTION.universes,
      collection: db.collection(FIREBASE_COLLECTION.universes)
    };
  }

  return {
    name: FIREBASE_COLLECTION.dashboards,
    collection: db.collectionGroup(FIREBASE_COLLECTION.dashboards)
  };
}

const UNIVERSES_WHERE_CLAUSE = {
  [UNIVERSE_PROPERTIES.universeId]: (query, value) => query.where(firebase.firestore.FieldPath.documentId(), FIREBASE_INDEXES.equal, value),
  [UNIVERSE_PROPERTIES.isStaging]: (query, value) => query.where(UNIVERSE_PROPERTIES.isStaging, FIREBASE_INDEXES.equal, value === UNIVERSE_PROPERTIES_OPTIONS.isStaging.staging),
  [UNIVERSE_PROPERTIES.accountId]: (query, value) => query.where(UNIVERSE_PROPERTIES.accountId, FIREBASE_INDEXES.equal, value),
};

const DASHBOARDS_WHERE_CLAUSE = {
  [DASHBOARD_PROPERTIES.dashboardId]: (query, value) => query.where(DASHBOARD_PROPERTIES_MODEL.dashboardId, FIREBASE_INDEXES.equal, value),
  [DASHBOARD_PROPERTIES.title]: (query, value) => query.where(DASHBOARD_PROPERTIES_MODEL.title, FIREBASE_INDEXES.equal, value.trim()),
  [DASHBOARD_PROPERTIES.userEmails]: (query, value) => query.where(DASHBOARD_PROPERTIES.userEmails, FIREBASE_INDEXES.array_contains, value),
  [DASHBOARD_PROPERTIES.contractModel]: (query, value) => {
    if (value === DASHBOARD_PROPERTIES_OPTIONS.contractModel.contract) {
      return query.where(DASHBOARD_PROPERTIES.expiresTrialDate, FIREBASE_INDEXES.equal, null);
    }

    if (value === DASHBOARD_PROPERTIES_OPTIONS.contractModel.trial) {
      return query.where(DASHBOARD_PROPERTIES.expiresTrialDate, FIREBASE_INDEXES.greater, firebase.firestore.Timestamp.fromDate(new Date('1999-01-01')))
        .orderBy(DASHBOARD_PROPERTIES.expiresTrialDate);
    }
  },
  [DASHBOARD_PROPERTIES.isPublished]: (query, value) => {
    if (value === DASHBOARD_PROPERTIES_OPTIONS.isPublished.published) {
      return query.where(DASHBOARD_PROPERTIES.isPublished, FIREBASE_INDEXES.equal, true);
    }

    if (value === DASHBOARD_PROPERTIES_OPTIONS.isPublished.inactive) {
      return query.where(DASHBOARD_PROPERTIES.isPublished, FIREBASE_INDEXES.equal, false);
    }
  },
  [DASHBOARD_PROPERTIES.createdDate]: (query, value) => dateFilter(query, value, DASHBOARD_PROPERTIES.createdDate),
  [DASHBOARD_PROPERTIES.updatedDate]: (query, value) => dateFilter(query, value, DASHBOARD_PROPERTIES.updatedDate),
};

function buildUniversesQueryByFilter(collection, filters) {
  const query = Object.entries(filters).reduce((query, [filter, value]) => {
    if (UNIVERSES_WHERE_CLAUSE.hasOwnProperty(filter)) return UNIVERSES_WHERE_CLAUSE[filter](query, value);
    return query;
  }, collection);

  return query;
}

function buildDashboardsQueryByFilter(collection, filters) {
  const query = Object.entries(filters).reduce((query, [filter, value]) => {
    if (DASHBOARDS_WHERE_CLAUSE.hasOwnProperty(filter)) return DASHBOARDS_WHERE_CLAUSE[filter](query, value);
    return query;
  }, collection);

  return query;
}

function buildQueryByFilter(selectedCollection, filter) {
  const clearedFilters = clearEmptyFilters(filter);
  if (!Object.keys(clearedFilters).length) return selectedCollection.collection;

  if (selectedCollection.name === FIREBASE_COLLECTION.universes) {
    return buildUniversesQueryByFilter(selectedCollection.collection, clearedFilters);
  }
  return buildDashboardsQueryByFilter(selectedCollection.collection, clearedFilters);
}

export async function getDashboards({ commit }, filter) {
  const selectedCollection = selectCollectionByFilter(filter);

  const collectionWithQuery = buildQueryByFilter(selectedCollection, filter);

  try {
    const collectionDocs = await collectionWithQuery.get();

    if (collectionDocs.empty) {
      mutateUniverses(commit, {});
      mutateSuccess(commit, false) ;
      mutateError(commit, false);
      changeInitialState(commit, false);
    }

    let dashboards = collectionDocs.docs;

    if (selectedCollection.name === FIREBASE_COLLECTION.universes) {
      const universesDashboards = await Promise.all(collectionDocs.docs.map(doc => {
        const clearedFilters = clearEmptyFilters(filter);
        const collectionQuery = buildDashboardsQueryByFilter(doc.ref.collection(FIREBASE_COLLECTION.dashboards), clearedFilters);
        return collectionQuery.get();
      }));

      dashboards = universesDashboards.map(universe => universe.docs).flat();
    }

    const dashboardList = await Promise.all(dashboards.map(async doc => {
      const universeDoc = await doc.ref.parent.parent.get();
      return {
        universeId: universeDoc.id,
        id: doc.id,
        data: doc.data(),
        ...universeDoc.data()
      };
    }));

    const universesDashboards = dashboardList
      .reduce((universesDashboards, dashboard) => {
        if (!universesDashboards.hasOwnProperty(dashboard.universeId)) {
          return Object.assign(universesDashboards, { [dashboard.universeId]: [dashboard] });
        }

        universesDashboards[dashboard.universeId].push(dashboard);
        return universesDashboards;
      }, {});

    mutateUniverses(commit, universesDashboards);
    Boolean(Object.keys(universesDashboards).length) ? mutateSuccess(commit, true) : mutateSuccess(commit, false) ;
    mutateError(commit, false);
    changeInitialState(commit, false);
  } catch (err) {
    console.error(err);
    mutateError(commit, false);
  }
}

function getDashboardRef(id) {
  const dashboardCollection = db.collectionGroup(FIREBASE_COLLECTION.dashboards);
  const dashboardQuery = DASHBOARDS_WHERE_CLAUSE[DASHBOARD_PROPERTIES.dashboardId](dashboardCollection, id);
  return dashboardQuery.get();
}

export async function dashboardExists({}, id) {
  const dashboardRef = await getDashboardRef(id);
  return !dashboardRef.empty;
}

export async function getDashboardData({}, id) {
  const dashboardRef = await getDashboardRef(id);

  if (dashboardRef.empty) return {};

  const universeRef = await dashboardRef.docs[0].ref.parent.parent.get();

  const dashboardData = {
    universeId: universeRef.id,
    id: dashboardRef.docs[0].id,
    ...dashboardRef.docs[0].data(),
  };

  return dashboardData;
}

export function getAccessDashboards({ commit }, filters) {
  const query = db.collectionGroup(FIREBASE_COLLECTION.dashboards)
    .where(DASHBOARD_PROPERTIES.createdByAccessPage, FIREBASE_INDEXES.equal, FIREBASE_BOOLEAN.true);

  try {
    query.onSnapshot(async (newData) => {
      saveUniverses([newData], commit, filters);
      return true;
    }
    );
    query.get()
      .then(async (data) => {
        saveUniverses([data], commit, filters);
        return true;
      })
      .catch(err => {
        console.error(err);
      });
  } catch (err) {
    console.error(err);
  }
}

export function getQuery(filters) {
  if (filters.universeId?.length || filters.accountId?.length || filters.isStaging?.length) {
    const query = db.collection(FIREBASE_COLLECTION.universes);

    const newQuery = validateUniverseFilter(query, filters);
    return newQuery;
  }
  const query = db.collectionGroup(FIREBASE_COLLECTION.dashboards);
  const newQuery = validateDashboardFilters(query, filters);
  return newQuery;
}

export async function getUniversesDashboards(data, filters) {
  const universesData = data.docs.map((doc) => {
    return db
      .collection(FIREBASE_COLLECTION.universes)
      .doc(doc.id)
      .collection(FIREBASE_COLLECTION.dashboards);
  });
  const universesDataFiltered = universesData.map(data => validateDashboardFilters(data, filters));
  const universesDataDashboards = await Promise.all(universesDataFiltered);
  const universesDataPromises = universesDataDashboards.map(data => data.get());
  const dashboardArrays = await Promise.all(universesDataPromises);
  return dashboardArrays;
}

export function hasDashboardList(filters) {
  const {
    accountId,
    isStaging,
  } = filters;
  const isAccOrStaging = Boolean(accountId?.length) || Boolean(isStaging?.length);
  return Boolean(isAccOrStaging) && !filters.universeId?.length;
}

export function hasUniverseList(filters) {
  const {
    accountId,
    isStaging,
  } = filters;
  const isAccOrStaging = Boolean(accountId?.length) || Boolean(isStaging?.length);
  return Boolean(isAccOrStaging) && filters.universeId?.length;
}

export async function validateUniverseFilter(query, filters) {
  let filteredQuery = query;

  if (Boolean(filters.universeId?.length)) {
    const newQuery = await filteredQuery
      .doc(filters.universeId)
      .collection(FIREBASE_COLLECTION.dashboards);
    const newQueryFiltered = await validateDashboardFilters(newQuery, filters);
    return newQueryFiltered;
  }
  if (Boolean(filters.accountId?.length)) {
    filteredQuery = accountIdFilter(filteredQuery, filters);
  }

  if (Boolean(filters.isStaging?.length)) {
    filteredQuery = isStagingFilter(filteredQuery, filters);
  }

  return filteredQuery;
}

export async function validateDashboardFilters(query, filters) {
  let filteredQuery = query;

  if (Object.keys(filters).length) {
    if (Boolean(filters.title?.length)) {
      filteredQuery = titleFilter(filteredQuery, filters);
    }

    if (Boolean(filters.userEmails?.length)) {
      filteredQuery = userEmailsFilter(filteredQuery, filters);
    }

    if (Boolean(filters.contractModel?.length)) {
      filteredQuery = contractFilter(filteredQuery, filters);
    }

    if (Boolean(filters.isPublished?.length)) {
      filteredQuery = isPublishedFilter(filteredQuery, filters);
    }

    filteredQuery = validateDateFilter(filteredQuery, filters);
  }

  return filteredQuery;
}

export function hasUniverseID(universe, filters) {
  let filteredUniverse = {};
  const hasUniverses = hasUniverseList(filters);
  if (hasUniverses) {
    if (Boolean(filters.accountId?.length)) {
      filteredUniverse = getAccountID(universe, filters);
    }

    if (Boolean(filters.isStaging?.length)) {
      filteredUniverse = getIsStaging(universe, filters);
    }
    return filteredUniverse;
  }
  return universe;
}

export function getAccountID(universe, filters) {
  const filteredUniverse = {};
  for (const [key, value] of Object.entries(universe)) {
    const filtered = value.filter((element) => element.accountId === filters.accountId);

    if (filtered.length) {
      filteredUniverse[key] = filtered;
      return filteredUniverse;
    }
  }
  return filteredUniverse;
}

export function getIsStaging(universe, filters) {
  const filteredUniverse = {};
  let filtered = [];

  for (const [key, value] of Object.entries(universe)) {
    if (filters.isStaging === DASHBOARD_PROPERTIES_OPTIONS.isStaging.staging) {
      filtered = value.filter((element) => element.isStaging === true);
    }

    if (filters.isStaging === DASHBOARD_PROPERTIES_OPTIONS.isStaging.prod) {
      filtered = value.filter((element) => element.isStaging !== true);
    }

    if (filters.isStaging === DASHBOARD_PROPERTIES_OPTIONS.isStaging.all) {
      return universe;
    }

    if (filtered.length) {
      filteredUniverse[key] = filtered;
    }

    return filteredUniverse;
  }
  return filteredUniverse;
}

export function dashboardIdFilter(universe, filters) {
  const filteredUniverse = {};
  if (Boolean(filters.dashboardId?.length)) {
    for (const [key, value] of Object.entries(universe)) {
      const filtered = value.filter((element) => element.id === filters.dashboardId);

      if (filtered.length) {
        filteredUniverse[key] = filtered;
      }
    } return filteredUniverse;
  } return universe;
}

export function removeActiveIsPublished(universe, filters) {
  const filteredUniverse = {};
  if (Boolean(filters.isPublished?.length)) {
    if (filters.isPublished === DASHBOARD_PROPERTIES_OPTIONS.isPublished.inactive) {
      for (const [key, value] of Object.entries(universe)) {
        const filteredValue = value.filter((element) => !Boolean(element.data.isPublished));
        if (filteredValue.length) {
          filteredUniverse[key] = filteredValue;
        }
      } return filteredUniverse;
    }
  } return universe;
}

export function accountIdFilter(data, filters) {
  return data.where(DASHBOARD_PROPERTIES.accountId, FIREBASE_INDEXES.equal, filters.accountId);
}

export function isStagingFilter(data, filters) {
  if (filters.isStaging === DASHBOARD_PROPERTIES_OPTIONS.isStaging.staging) {
    return data.where(DASHBOARD_PROPERTIES.isStaging, FIREBASE_INDEXES.equal, true);
  } else if (filters.isStaging === DASHBOARD_PROPERTIES_OPTIONS.isStaging.prod) {
    return data.where(DASHBOARD_PROPERTIES.isStaging, FIREBASE_INDEXES.equal, false);
  } return data;
}

export function titleFilter(data, filters) {
  return data.where(DASHBOARD_PROPERTIES.title, FIREBASE_INDEXES.equal, filters.title);
}

export function userEmailsFilter(data, filters) {
  return data.where(DASHBOARD_PROPERTIES.userEmails, FIREBASE_INDEXES.array_contains, filters.userEmails);
}

export function contractFilter(data, filters) {
  if (filters.contractModel === DASHBOARD_PROPERTIES_OPTIONS.contractModel.contract) {
    return data.where(DASHBOARD_PROPERTIES.expiresTrialDate, FIREBASE_INDEXES.equal, null);
  } else if (filters.contractModel === DASHBOARD_PROPERTIES_OPTIONS.contractModel.trial) {
    return data.where(DASHBOARD_PROPERTIES.expiresTrialDate, FIREBASE_INDEXES.not_equal, null);
  } return data;
}

export function isPublishedFilter(data, filters) {
  if (filters.isPublished === DASHBOARD_PROPERTIES_OPTIONS.isPublished.published) {
    return data.where(DASHBOARD_PROPERTIES.isPublished, FIREBASE_INDEXES.equal, true);
  } return data;
}

export function validateDateFilter(data, filters) {
  let newData = data;

  if (Boolean(filters.createdDate?.length)) {
    const { createdDate } = DASHBOARD_PROPERTIES;
    const dataFormatted = formatDate(filters.createdDate);
    newData = dateFilter(newData, dataFormatted, createdDate);
    return newData;
  }

  if (Boolean(filters.updatedDate?.length)) {
    const { updatedDate } = DASHBOARD_PROPERTIES;
    const dataFormatted = formatDate(filters.updatedDate);
    newData = dateFilter(newData, dataFormatted, updatedDate);
    return newData;
  }
  return newData;
}

export function hasRangeDateFilters(universe, filters) {
  const filteredUniverse = {};
  if (Boolean(filters.createdDate?.length) && Boolean(filters.updatedDate?.length)) {
    const dateFormatted = formatDate(filters.updatedDate);
    const convertedDate = dateFormatted.map(date => firebase.firestore.Timestamp.fromDate(new Date(date)).toDate());
    for (const [key, value] of Object.entries(universe)) {
      const filteredDate = value.filter((element) => {
        if (Boolean(element.data.audit.updatedDate)) {
          const updatedDate = element.data.audit.updatedDate.toDate();
          return updatedDate >= convertedDate[0] && updatedDate <= convertedDate[1];
        } return false;
      });

      if (filteredDate.length) {
        filteredUniverse[key] = filteredDate;
      }
    } return filteredUniverse;
  } return universe;
}

export function formatDate(filter) {
  let newValues = [];

  if (filter.length === 1) {
    newValues = [`${filter[0]} 00:00:00`, `${filter[0]} 23:59:59`];
  } else if (filter.length === 2) {
    newValues = [`${filter[0]} 00:00:00`, `${filter[1]} 23:59:59`];
  }

  return newValues;
}

function getDayRange(singleDateArray) {
  return singleDateArray.map(date => {
    const startDate = new Date(`${date}T00:00:00`);
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date(`${date}T00:00:00`);
    endDate.setHours(23, 59, 59, 999);

    return [
      firebase.firestore.Timestamp.fromDate(new Date(startDate)),
      firebase.firestore.Timestamp.fromDate(new Date(endDate))
    ];
  })
    .flat();
}

export function dateFilter(data, values, dataType) {
  const newData = data;

  const formattedDate = values.length > 1
    ? values.map(date => firebase.firestore.Timestamp.fromDate(new Date(`${date}T00:00:00`)))
    : getDayRange(values);

  const orderedDate = formattedDate.sort((date1, date2) => date1 - date2);
  const endDatePlusOneDay = new Date(orderedDate[1].toDate().getTime() + 86400000);

  return values.length > 1
    ? newData.orderBy(DASHBOARD_DATE_PROPERTIES[dataType]).startAt(orderedDate[0]).endAt(firebase.firestore.Timestamp.fromDate(endDatePlusOneDay))
    : newData.orderBy(DASHBOARD_DATE_PROPERTIES[dataType]).startAt(orderedDate[0]).endAt(orderedDate[1]);
}

export function sanatizeUniverses(universe, filters) {
  let filteredUniverse = universe;

  filteredUniverse = dashboardIdFilter(filteredUniverse, filters);
  filteredUniverse = hasRangeDateFilters(filteredUniverse, filters);
  filteredUniverse = removeActiveIsPublished(filteredUniverse, filters);
  filteredUniverse = hasUniverseID(filteredUniverse, filters);

  return filteredUniverse;
}
export function mutateUniverses(commit, universe) {
  commit(MUTATE, {
    property: 'universes',
    with: universe
  });
}

export function mutateError(commit, error) {
  commit(MUTATE, {
    property: 'error',
    with: error
  });
}

export function mutateSuccess(commit, success) {
  commit(MUTATE, {
    property: 'success',
    with: success
  });
}

export function changeInitialState(commit, newState) {
  commit(MUTATE, {
    property: 'initialState',
    with: newState
  });
}

export async function createDashboard({ commit }, {
  universeId,
  accountId,
  dashboard,
  isStaging,
}) {
  try {
    const universes = db.collection('universes');
    const universe = await universes.doc(universeId).get();
    // generate token
    const integrationToken = CryptoJS.MD5(accountId).toString();
    if (!universe.exists) {
      // Create universe
      await universes.doc(universeId).set({
        isStaging,
        accountId,
        integrationToken,
      });
    }

    // Create dashboard
    const dashboardRef = await db
      .collection('universes')
      .doc(universeId)
      .collection('dashboards')
      .add(dashboard);

    // Set the dashboard ID
    await db
      .collection('universes')
      .doc(universeId)
      .collection('dashboards')
      .doc(dashboardRef.id)
      .set({
        id: dashboardRef.id,
      }, {
        merge: true,
      });

    // Save universes if applicable
    const dashboardData = dashboardRef.get();
    if (dashboardData.exists) {
      saveUniverses(dashboardData, commit);
      return true;
    }

    return false;
  } catch (err) {
    console.error(err);
  }
}

export async function editPermission(store, {
  universeId,
  dashboardId,
  users = [],
  expiresTrialDate,
  audit,
  userEmails,
}) {
  try {
    return db
      .collection('universes')
      .doc(universeId)
      .collection('dashboards')
      .doc(dashboardId)
      .set({
        users,
        expiresTrialDate,
        userEmails,
        audit,
      }, {
        merge: true,
      });
  } catch (err) {
    console.error(err);
  }
}

export async function deleteDashboard({ commit }, {
  accountId,
  universeId,
  dashboardId,
  name,
  email,
}) {
  try {
    const logData = {
      action: 'delete',
      accountId,
      universeId,
      dashboardId,
      userName: name,
      userEmail: email,
    };

    const logCreated = await createLog(logData);

    if (!logCreated) throw new Error('log is not created');

    return await db
      .collection('universes')
      .doc(universeId)
      .collection('dashboards')
      .doc(dashboardId)
      .delete();
  } catch (err) {
    mutateError(commit, true);
    await updateStatusErrorLog(dashboardId);
    console.error(err);
  }
}
